import "@/App.css";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Toaster } from "sonner";
import { StormLayer, stormLevelOf } from "./components/StormLayer";
import { StormCelebration } from "./components/StormCelebration";
import { StormHistory } from "./components/StormHistory";
import { IntroDesktop } from "./components/IntroDesktop";
import { IntroMobile } from "./components/IntroMobile";
import { SpaceBackground } from "./components/SpaceBackground";
import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { Marquee } from "./components/Marquee";
import { Lore } from "./components/Lore";
import { ChartReplay } from "./components/ChartReplay";
import { LiveChart } from "./components/LiveChart";
import { Gallery } from "./components/Gallery";
import { VideoGallery } from "./components/VideoGallery";
import { Tweets } from "./components/Tweets";
import { Footer } from "./components/Footer";
import { MusicPlayer } from "./components/MusicPlayer";
import { AirdropSummary } from "./components/AirdropSummary";

function App() {
  const [entered, setEntered] = useState(false);
  const [stats, setStats] = useState(null);
  const [volume, setVolume] = useState(null);
  const [serverTier, setServerTier] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const lastEventId = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/stats`);
        if (!alive || !data.ok) return;
        setStats(data);
        setVolume(Number(data.volume24h));
        if (data.stormTier != null) setServerTier(data.stormTier);
        const ev = data.stormEvent;
        // Snapshot the current event id as the baseline so we never fire a
        // celebration on this static load. (Polling is paused; this only runs once.)
        lastEventId.current = ev?.id ?? "none";
      } catch {
        /* keep last */
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!entered) return;
    const test = new URLSearchParams(window.location.search).get("stormtest");
    if (test) {
      const to = Math.min(3, Math.max(1, parseInt(test, 10) || 2));
      const t = setTimeout(() => setCelebration({ id: "test", from: to - 1, to }), 1200);
      return () => clearTimeout(t);
    }
  }, [entered]);

  // Mobile-only: mark <body> as `is-scrolling` while scroll is active so we
  // can pause heavy CSS animations for a smoother scroll. Debounced by 140ms
  // after the last scroll event.
  useEffect(() => {
    if (!entered) return undefined;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile) return undefined;
    let timer = null;
    let active = false;
    const onScroll = () => {
      if (!active) {
        document.body.classList.add("is-scrolling");
        active = true;
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        document.body.classList.remove("is-scrolling");
        active = false;
      }, 140);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (timer) clearTimeout(timer);
      document.body.classList.remove("is-scrolling");
    };
  }, [entered]);

  const stormLevel = serverTier ?? stormLevelOf(volume ?? 0);

  if (!entered) {
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    return isMobile ? (
      <IntroMobile onEnter={() => setEntered(true)} stormLevel={stormLevel} />
    ) : (
      <IntroDesktop onEnter={() => setEntered(true)} stormLevel={stormLevel} />
    );
  }

  return (
    <div className="relative min-h-screen" data-testid="curb-landing-page">
      <SpaceBackground />
      <StormLayer level={stormLevel} />
      {celebration && <StormCelebration event={celebration} onDone={() => setCelebration(null)} />}
      <div className={`relative z-10 ${celebration ? "storm-shake" : ""}`}>
        <Navbar />
        <Hero stormVolume={volume} stormLevel={stormLevel} stats={stats} />
        <Marquee />
        <Lore />
        <Gallery />
        <VideoGallery />
        <AirdropSummary />
        <ChartReplay />
        <LiveChart />
        <Tweets />
        <StormHistory />
        <Footer />
      </div>
      <MusicPlayer />
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#0a0714",
            border: "1px solid rgba(20,241,149,0.5)",
            color: "#fff",
          },
        }}
      />
    </div>
  );
}

export default App;
